<template>
    <modal size="md" @close="reset(); $emit('close')" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title" v-if="deferredComp">Edit Deferred Compensation</h3>
            <h3 class="modal-title" v-else>Add Deferred Compensation</h3>
        </template>

        <slot>
            <div class="mb-2">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Employee: </span>{{ employee.full_name }}</div>
                    <div><span class="text-muted">Company: </span>{{ employee.company.name }}</div>
                    <div v-if="deferredComp"><span class="text-muted">Plan: </span>{{ deferredComp.deferred_comp_plan.display_name }}</div>
                </div>
            </div>

            <div class="form-row" v-if="!deferredComp">
                <div class="col">
                    <form-input label="Plan" v-model="formData.deferred_comp_plan" :errors="errors.deferred_comp_plan" type="select" :options="deferredCompPlanOptions" :noblank="true"/>
                </div>
            </div>

            <div class="form-row">
                <div class="col col-6">
                    <form-input label="Contribution Method" v-model="formData.contribution_method" :errors="errors.contribution_method" type="select" :options="contributionMethodOptions" :noblank="true"/>
                </div>
            </div>

            <div class="form-row">
                <div class="col col-6" v-if="formData.contribution_method == 'amount'">
                    <form-input label="Employee Contribution" helper-text="per pay period" v-model="formData.employee_contribution_amount" :errors="errors.employee_contribution_amount" type="currency" prefix="$"/>
                </div>

                <div class="col col-6" v-else>
                    <form-input label="Employee Contribution" v-model="formData.employee_contribution_pct" :errors="errors.employee_contribution_pct" type="decimal" maxlength="6" prefix="%"/>
                </div>
            </div>

            <div class="form-row mb-2" v-if="selectedPlanSupportsCatchup">
                <div class="col">
                    <form-input
                        label="Highly Paid Individual (HPI)"
                        v-model="formData.is_hpi"
                        :errors="errors.is_hpi"
                        type="checkbox"
                        helper-text="Set when the employee's prior-year W-2 Box 3 wages from this employer exceeded the HPI threshold ($150,000 for 2026). Their catch-up contributions are then made on a Roth basis; regular contributions are unaffected."
                    />
                </div>
            </div>

            <div class="form-row">
                <div class="col col-6">
                    <form-input label="Effective Date" v-model="formData.effective_on" :errors="errors.effective_on" type="date" />
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>

                <button type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import moment from 'moment'

export default {
    props: ['client', 'employee', 'deferredComp', 'latestMutation', 'clientDeferredCompPlans'],
    mixins: [FormMixin, ModalMixin],
    components: {},
    emits: ['updated', 'close', ],
    data() {
        const today = moment()

        const dateValidation = (this.deferredComp) ? {earliest: today, tooEarly: "can't be before tomorrow."} : true

        const data = {
            formData: this.makeFormData(),
            formRules: {
                effective_on: {presence: {allowEmpty: false}, date: dateValidation},
            },
            formLabels: {
            },
            errors: {}
        }

        return data
    },
    computed: {
        company() {
            return this.client.companies.find(c => c.id == this.employee.company_id)
        },
        contributionMethodOptions() {
            return [
                {text: 'Percentage of pay', value: 'percent'},
                {text: 'Flat dollar amount', value: 'amount'},
            ]
        },
        selectedPlan() {
            const planId = this.deferredComp ? this.deferredComp.deferred_comp_plan_id : this.formData.deferred_comp_plan
            if (!planId) {return null}

            return this.clientDeferredCompPlans.find(p => p.id == planId)
        },
        selectedPlanSupportsCatchup() {
            return !!(this.selectedPlan && this.selectedPlan.catchup_company_deduction_type_id)
        },
        deferredCompPlanOptions() {
            const result = []

            // TODO KB ES further filter by position if not admin; filter out plans that already exist
            this.clientDeferredCompPlans.filter(p => p.company_id == this.employee.company_id).forEach(p => {
                result.push({text: p.display_name, value: p.id})
            })

            result.sort((a, b) => a.text > b.text ? 1 : -1)

            return result
        },
    },
    methods: {
        makeFormData() {
            const editFromObj = this.latestMutation || this.deferredComp
            const data = {
                deferred_comp_id: this.deferredComp ? this.deferredComp.id : null,
                deferred_comp_plan: this.deferredComp ? this.deferredComp.deferred_comp_plan_id : null,
                // fields that can be changed are pulled from latest mutation if it exists:
                contribution_method: editFromObj?.contribution_method ? editFromObj.contribution_method : 'percent',
                employee_contribution_pct: editFromObj ? editFromObj.employee_contribution_pct : '',
                employee_contribution_amount: editFromObj ? editFromObj.employee_contribution_amount : '',
                is_hpi: editFromObj ? !!editFromObj.is_hpi : false,
                effective_on: editFromObj?.effective_on ? editFromObj.effective_on : moment().format('YYYY-MM-DD'),
            }

            return data
        },
        getFormRules() {
            const rules = JSON.parse(JSON.stringify(this.formRules))

            // Only the field matching the chosen method is submitted as meaningful, so
            // only that one is required.
            if (this.formData.contribution_method == 'amount') {
                rules.employee_contribution_amount = {presence: {allowEmpty: false}, numericality: {greaterThan: 0}}
            } else {
                rules.employee_contribution_pct = {presence: {allowEmpty: false}, numericality: {greaterThan: 0, lessThanOrEqualTo: 100}}
            }

            return rules
        },
        validate() {
            let isValid = FormMixin.methods.validate.call(this)

            return isValid
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            const data = this.getFormData()

            this.$store.dispatch('START_LOADING')
            return this.$api.post(`/clients/${this.client.id}/employees/${this.employee.id}/deferred-comp-mutations`, data).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully saved deferred compensation.`)
                this.$emit('updated', resp)
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = (errors.__all__ || [])
                this.$bus.showError(errors.__all__)
            })
        }
    }
}
</script>
